import { Injectable, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';
import { JepRequestService } from '@jepto/shared/services/jep-request/jep-request.service';
import { JepSnackbarComponent } from '@jepto/shared/components/jep-snackbar/jep-snackbar.component';
import { isErrorExistingFromGQLResponse } from '@jepto/shared/utils/collection';
import { warn } from '@jepto/shared/utils/logger';
import { CardConfig } from '../models/card-model';
import {
	CardPersistenceState,
	PersistenceOptions,
	DEFAULT_PERSISTENCE_OPTIONS,
	PersistenceStateChangeEvent,
	ChangePersistedEvent,
	RevisionConflictError,
	RestoreNavigationChangedEvent,
} from './card-persistence.types';
import {
	mutationUpdateCardWithVersion,
	UpdateCardInput,
	UpdateCardResponse,
} from '../graphql/mutations/update-card-with-version';
import { cardPatchToGqlInput } from '../utils/card-patch-to-gql-input.util';
import {
	mutationRestoreCardVersion,
	RestoreCardVersionVariables,
} from '../graphql/mutations/restore-card-version';
import { queryGetCardRevisions } from '../graphql/queries/get-card-revisions';

/**
 * Handles card persistence with optimistic locking and revision management.
 *
 * Responsibilities:
 * - Track server revision per card
 * - Queue changes for auto-save
 * - Handle revision conflicts via JepSnackbar
 * - Server-side undo/redo via restoreVersion
 * - Emit state change events for UI feedback
 */
@Injectable({
	providedIn: 'root',
})
export class CardPersistenceService {
	private requestService = inject(JepRequestService);
	private snackBar = inject(MatSnackBar);

	/**
	 * Track persistence state per card.
	 * Key: cardId
	 */
	private state = new Map<string, CardPersistenceState>();

	/** Serializes `flush` per card so concurrent saves cannot reuse a stale revision. */
	private flushChains = new Map<string, Promise<void>>();

	/**
	 * Event callbacks for state changes.
	 */
	private stateChangeCallbacks: Array<
		(event: PersistenceStateChangeEvent) => void
	> = [];

	/**
	 * Event callbacks for successful persistence.
	 */
	private persistedCallbacks: Array<(event: ChangePersistedEvent) => void> =
		[];

	private restoreNavigationCallbacks: Array<
		(event: RestoreNavigationChangedEvent) => void
	> = [];

	/**
	 * Default persistence options.
	 */
	private options: PersistenceOptions = DEFAULT_PERSISTENCE_OPTIONS;

	/**
	 * Initialize a card for persistence tracking.
	 *
	 * @param cardId - Unique card identifier
	 * @param revisions - Initial server revision (from loaded card's revisions field)
	 * @param options - Optional persistence options override
	 */
	initializeCard(
		cardId: string,
		revisions: number | null,
		options?: Partial<PersistenceOptions>
	): void {
		this.clearTimers(cardId);

		this.state.set(cardId, {
			cardId,
			serverRevisions: revisions,
			contentSnapshotVersion: revisions,
			redoTargets: [],
			pendingChange: null,
			retryQueue: [],
			status: 'idle',
			debounceTimer: null,
			maxWaitTimer: null,
		});

		if (options) {
			this.options = { ...DEFAULT_PERSISTENCE_OPTIONS, ...options };
		}
	}

	/**
	 * Re-reads the card's current `revisions` from the server and adopts it
	 * forward-only into the persistence tracker.
	 */
	async refreshServerRevisions(cardId: string): Promise<number | null> {
		const revisions = await this.#fetchServerRevisions(cardId);
		if (revisions !== null) {
			this.syncServerRevisions(cardId, revisions);
		}
		return revisions;
	}

	/**
	 * Updates the tracked server revision without clearing pending edits or timers.
	 * Only moves revision forward (never downgrades after a successful debounced save).
	 */
	syncServerRevisions(cardId: string, revisions: number): void {
		if (!Number.isFinite(revisions)) {
			return;
		}
		const currentState = this.getState(cardId);
		if (
			currentState.serverRevisions === null ||
			revisions > currentState.serverRevisions
		) {
			currentState.serverRevisions = revisions;
		}
		if (
			currentState.contentSnapshotVersion === null &&
			Number.isFinite(revisions)
		) {
			currentState.contentSnapshotVersion = revisions;
		}
	}

	/**
	 * Queue a change for persistence (debounced auto-save).
	 *
	 * @param cardId - Card identifier
	 * @param patch - Partial configuration change
	 */
	queueChange(cardId: string, patch: Partial<CardConfig>): void {
		const currentState = this.getState(cardId);
		currentState.redoTargets = [];
		this.notifyRestoreNavigation(cardId);

		// Merge with existing pending change if any
		const pending = currentState.pendingChange;
		const mergedPatch = this.mergePendingCardPatch(pending?.patch, patch);

		// Update pending change
		currentState.pendingChange = {
			patch: mergedPatch,
			timestamp: Date.now(),
		};

		// Set status to idle (will change to saving when debounce fires)
		this.updateStatus(cardId, 'idle');

		// Clear existing timers
		this.clearTimers(cardId);

		// Set up debounce timer
		currentState.debounceTimer = setTimeout(() => {
			void this.flush(cardId);
		}, this.options.debounceMs);

		// Set up max wait timer
		currentState.maxWaitTimer = setTimeout(() => {
			void this.flush(cardId);
		}, this.options.maxDelayMs);
	}

	/**
	 * Force immediate persistence of pending changes.
	 *
	 * @param cardId - Card identifier
	 * @returns Promise resolving when save completes
	 */
	async flush(cardId: string): Promise<void> {
		const prior = this.flushChains.get(cardId) ?? Promise.resolve();
		const next = prior
			.catch(() => undefined)
			.then(() => this.#flushOnce(cardId));
		this.flushChains.set(cardId, next);
		await next;
	}

	/** Single save attempt; concurrent callers are queued via {@link flush}. */
	async #flushOnce(cardId: string): Promise<void> {
		const currentState = this.getState(cardId);

		// Clear timers
		this.clearTimers(cardId);

		// Check if there's anything to save
		if (!currentState.pendingChange) {
			return;
		}

		// Check if we have a server revision
		if (currentState.serverRevisions === null) {
			// Card hasn't been saved to server yet - skip
			currentState.pendingChange = null;
			return;
		}

		// Move pending to in-flight
		const change = currentState.pendingChange;
		currentState.pendingChange = null;
		const revisionsForSave = currentState.serverRevisions;

		// Update status
		this.updateStatus(cardId, 'saving');

		try {
			await this.persistChange(cardId, change.patch, revisionsForSave);
		} catch (error) {
			// Check if it's a revision conflict
			if (this.isRevisionConflictError(error)) {
				this.handleConflict(cardId, error, change.patch);
			} else {
				// Non-conflict failures (network, server, missing response, etc.)
				// were previously silent for the user. Surface via JepSnackbar
				// using the server-provided message (extracted from GraphQL
				// `errors[0].message` in `persistChange`).
				this.updateStatus(cardId, 'error', error as Error);
				const message =
					(error instanceof Error && error.message) ||
					"Couldn't save your card changes. Please try again.";
				this.showSaveError(message);
			}
		} finally {
			if (this.getState(cardId).pendingChange) {
				void this.flush(cardId);
			}
		}
	}

	/**
	 * Restore a historical version (server-side undo/redo).
	 *
	 * @param cardId - Card identifier
	 * @param targetVersion - Version to restore (1 to current revisions)
	 * @returns CardItem with new revision
	 */
	// fallow-ignore-next-line complexity
	async restoreVersion(
		cardId: string,
		targetVersion: number
	): Promise<CardConfig> {
		const currentState = this.getState(cardId);

		if (currentState.serverRevisions === null) {
			throw new Error('Cannot restore: card has no server revision');
		}

		this.updateStatus(cardId, 'saving');

		try {
			const variables: RestoreCardVersionVariables = {
				cardId,
				version: targetVersion,
				revisions: currentState.serverRevisions,
			};

			const gqlResponse = (await firstValueFrom(
				this.requestService.mutate({
					mutation: mutationRestoreCardVersion,
					variables,
				})
			)) as {
				data?: {
					restoreCardVersion?: CardConfig & {
						revisions?: number;
						dateUpdated?: string;
					};
				};
				errors?: Array<{ message?: string }>;
			};

			// `JepRequestService.mutate()` always resolves — surface server
			// errors via `gqlResponse.errors[0].message` (project convention).
			if (isErrorExistingFromGQLResponse(gqlResponse)) {
				const msg =
					(Array.isArray(gqlResponse.errors) &&
						gqlResponse.errors[0]?.message) ||
					"Couldn't restore the card version";
				throw new Error(msg);
			}

			const result = gqlResponse.data?.restoreCardVersion;

			if (!result) {
				throw new Error("Couldn't restore the card version");
			}

			// Update server revision (result contains incremented revisions)
			currentState.serverRevisions = result.revisions ?? null;
			currentState.lastSavedAt = result.dateUpdated;

			this.updateStatus(cardId, 'idle');

			// Notify listeners
			this.notifyPersisted({
				cardId: result.cardId ?? cardId,
				revisions: result.revisions ?? 0,
				updatedAt: result.dateUpdated ?? new Date().toISOString(),
				patch: {}, // Restoration is a server operation
			});

			return result;
		} catch (error) {
			if (this.isRevisionConflictError(error)) {
				this.handleConflict(cardId, error, {});
				throw error;
			}

			this.updateStatus(cardId, 'error', error as Error);
			const message =
				(error instanceof Error && error.message) ||
				"Couldn't restore the card version. Please try again.";
			this.showSaveError(message);
			throw error;
		}
	}

	/**
	 * Get current server revision for a card.
	 *
	 * @param cardId - Card identifier
	 * @returns Current server revision or null
	 */
	getCurrentRevisions(cardId: string): number | null {
		return this.getState(cardId).serverRevisions;
	}

	getContentSnapshotVersion(cardId: string): number | null {
		return this.getState(cardId).contentSnapshotVersion;
	}

	canUndo(cardId: string): boolean {
		const version = this.getState(cardId).contentSnapshotVersion;
		return version !== null && version > 1;
	}

	canRedo(cardId: string): boolean {
		return this.getState(cardId).redoTargets.length > 0;
	}

	async undo(cardId: string): Promise<CardConfig> {
		const state = this.getState(cardId);
		const current = state.contentSnapshotVersion;
		if (current === null || current <= 1) {
			throw new Error('Cannot undo: no previous version exists');
		}
		state.redoTargets.push(current);
		const target = current - 1;
		const restored = await this.restoreVersion(cardId, target);
		state.contentSnapshotVersion = target;
		this.notifyRestoreNavigation(cardId);
		return restored;
	}

	async redo(cardId: string): Promise<CardConfig> {
		const state = this.getState(cardId);
		const target = state.redoTargets.pop();
		if (target === undefined) {
			throw new Error('Cannot redo: no forward history');
		}
		const restored = await this.restoreVersion(cardId, target);
		state.contentSnapshotVersion = target;
		this.notifyRestoreNavigation(cardId);
		return restored;
	}

	async undoSteps(cardId: string, steps: number): Promise<CardConfig> {
		const state = this.getState(cardId);
		const current = state.contentSnapshotVersion;
		if (current === null) {
			throw new Error('Cannot undo: Invalid version number');
		}
		if (steps >= current) {
			throw new Error(
				`Cannot undo ${steps} steps: only ${current - 1} previous versions exist`
			);
		}
		state.redoTargets = [];
		const target = current - steps;
		const restored = await this.restoreVersion(cardId, target);
		state.contentSnapshotVersion = target;
		this.notifyRestoreNavigation(cardId);
		return restored;
	}

	/**
	 * Get current persistence status for a card.
	 *
	 * @param cardId - Card identifier
	 * @returns Current status
	 */
	getStatus(cardId: string): CardPersistenceState['status'] {
		return this.getState(cardId).status;
	}

	/**
	 * Check if card has pending changes.
	 *
	 * @param cardId - Card identifier
	 * @returns Whether there are unsaved changes
	 */
	hasPendingChanges(cardId: string): boolean {
		const state = this.getState(cardId);
		return state.pendingChange !== null || state.status === 'saving';
	}

	/**
	 * Subscribe to persistence state changes.
	 *
	 * @param callback - Function to call on state change
	 * @returns Unsubscribe function
	 */
	onStateChange(
		callback: (event: PersistenceStateChangeEvent) => void
	): () => void {
		this.stateChangeCallbacks.push(callback);
		return () => {
			const index = this.stateChangeCallbacks.indexOf(callback);
			if (index > -1) {
				this.stateChangeCallbacks.splice(index, 1);
			}
		};
	}

	/**
	 * Subscribe to successful persistence events.
	 *
	 * @param callback - Function to call when change is persisted
	 * @returns Unsubscribe function
	 */
	onChangePersisted(
		callback: (event: ChangePersistedEvent) => void
	): () => void {
		this.persistedCallbacks.push(callback);
		return () => {
			const index = this.persistedCallbacks.indexOf(callback);
			if (index > -1) {
				this.persistedCallbacks.splice(index, 1);
			}
		};
	}

	/**
	 * Subscribe to undo/redo availability changes for a card.
	 */
	onRestoreNavigationChanged(
		callback: (event: RestoreNavigationChangedEvent) => void
	): () => void {
		this.restoreNavigationCallbacks.push(callback);
		return () => {
			const index = this.restoreNavigationCallbacks.indexOf(callback);
			if (index > -1) {
				this.restoreNavigationCallbacks.splice(index, 1);
			}
		};
	}

	/**
	 * Clean up persistence state for a card.
	 *
	 * @param cardId - Card identifier
	 */
	cleanup(cardId: string): void {
		this.clearTimers(cardId);
		this.state.delete(cardId);
		this.flushChains.delete(cardId);
	}

	async #fetchServerRevisions(cardId: string): Promise<number | null> {
		try {
			const gqlResponse = (await firstValueFrom(
				this.requestService.query({
					query: queryGetCardRevisions,
					variables: { cardId },
					fetchPolicy: 'network-only',
				})
			)) as {
				data?: { getItem?: { card?: { revisions?: number } } };
				errors?: unknown[];
			};
			if (isErrorExistingFromGQLResponse(gqlResponse)) {
				warn(
					'CardPersistenceService: getCardRevisions returned GraphQL errors',
					{ cardId }
				);
				return null;
			}
			const revisions = gqlResponse.data?.getItem?.card?.revisions;
			return typeof revisions === 'number' && Number.isFinite(revisions)
				? revisions
				: null;
		} catch (error) {
			warn(
				'CardPersistenceService: getCardRevisions request failed',
				{ cardId, error }
			);
			return null;
		}
	}

	private resolveUpdateCardGqlFailure(
		gqlResponse: { errors?: unknown[] },
		revisions: number
	): never {
		const firstError = Array.isArray(gqlResponse.errors)
			? (gqlResponse.errors[0] as {
					message?: string;
					errorType?: string;
					errorInfo?: { currentRevisions?: number } | null;
				})
			: undefined;
		const msg = firstError?.message || "Couldn't save your card changes";
		if (firstError?.errorType === 'VERSION_CONFLICT') {
			const serverRevisions = firstError.errorInfo?.currentRevisions;
			const currentRevisionsKnown = typeof serverRevisions === 'number';
			throw {
				type: 'REVISION_CONFLICT',
				message: msg,
				currentRevisions: currentRevisionsKnown
					? serverRevisions
					: revisions + 1,
				currentRevisionsKnown,
				currentData: {} as CardConfig,
			} satisfies RevisionConflictError;
		}
		throw new Error(msg);
	}

	/**
	 * Persist a change to the server.
	 */
	private async persistChange(
		cardId: string,
		patch: Partial<CardConfig>,
		revisions: number
	): Promise<void> {
		const gqlPatch = cardPatchToGqlInput(patch);
		if (Object.keys(gqlPatch).length === 0) {
			return;
		}

		const input: UpdateCardInput = {
			cardId: cardId,
			patch: gqlPatch,
			revisions,
		};

		const gqlResponse = (await firstValueFrom(
			this.requestService.mutate({
				mutation: mutationUpdateCardWithVersion,
				variables: { input },
			})
		)) as {
			data?: { updateCard?: UpdateCardResponse };
			errors?: Array<{ message?: string }>;
		};

		// `JepRequestService.mutate()` always resolves — server/network failures
		// arrive as `gqlResponse.errors`, not as a thrown promise. Surface the
		// server-provided message (matches the convention in
		// `jep-theme-mutations.service.ts`, `report-api.service.ts`, etc.).
		if (isErrorExistingFromGQLResponse(gqlResponse)) {
			this.resolveUpdateCardGqlFailure(gqlResponse, revisions);
		}

		const result = gqlResponse.data?.updateCard;

		if (!result) {
			throw new Error("Couldn't save your card changes");
		}

		// Update server revision
		const currentState = this.getState(cardId);
		const savedAt = result.dateUpdated?.trim() || new Date().toISOString();
		currentState.serverRevisions = result.revisions;
		currentState.lastSavedAt = savedAt;
		currentState.contentSnapshotVersion = result.revisions;
		currentState.redoTargets = [];

		this.updateStatus(cardId, 'idle');

		// Notify listeners
		this.notifyPersisted({
			cardId: result.cardId,
			revisions: result.revisions,
			updatedAt: savedAt,
			patch,
		});
		this.notifyRestoreNavigation(cardId);
	}

	/**
	 * Show a transient error snackbar using the shared `JepSnackbarComponent`.
	 * Matches the app-wide convention for save/network failure surfaces.
	 */
	private showSaveError(text: string): void {
		this.snackBar.openFromComponent(JepSnackbarComponent, {
			duration: 5000,
			data: { text, status: 'error' },
		});
	}

	/**
	 * Re-queues a conflicted patch against the server's current revision.
	 * When the conflict carried no revision, refetch it; the `revisions + 1`
	 * guess fails again whenever the card is more than one revision behind.
	 */
	private async retryAfterConflict(
		cardId: string,
		error: RevisionConflictError,
		attemptedPatch: Partial<CardConfig>
	): Promise<void> {
		const fetched =
			error.currentRevisionsKnown === false
				? await this.#fetchServerRevisions(cardId)
				: null;
		this.getState(cardId).serverRevisions =
			fetched ?? error.currentRevisions;
		this.queueChange(cardId, attemptedPatch);
	}

	/**
	 * Handle revision conflict with JepSnackbar.
	 */
	private handleConflict(
		cardId: string,
		error: RevisionConflictError,
		attemptedPatch: Partial<CardConfig>
	): void {
		const currentState = this.getState(cardId);

		// Update state with conflict info
		currentState.lastError = error;
		this.updateStatus(cardId, 'conflict', error);

		// Add to retry queue if retries remain
		if (currentState.retryQueue.length < this.options.maxRetries) {
			currentState.retryQueue.push({
				id: `${cardId}:${Date.now()}`,
				patch: attemptedPatch,
				revisions: error.currentRevisions,
				retryCount: currentState.retryQueue.length + 1,
			});
		}

		// Show conflict resolution UI
		if (this.options.showConflictUI) {
			const snackbarRef = this.snackBar.openFromComponent(
				JepSnackbarComponent,
				{
					duration: 0, // Persistent until user action
					data: {
						text: 'Your changes conflict with a newer revision. Retry or discard?',
						status: 'warning',
						withButton: true,
						customTextButton: 'RETRY',
					},
				}
			);

			snackbarRef.instance.onClickSnackbar.subscribe(() => {
				// Retry: adopt the real server revision and re-apply
				currentState.retryQueue = [];
				void this.retryAfterConflict(cardId, error, attemptedPatch);
				snackbarRef.dismiss();
			});

			// Handle dismiss (user chose to discard)
			snackbarRef.afterDismissed().subscribe(() => {
				if (currentState.status === 'conflict') {
					// User dismissed without retry - clear retry queue
					currentState.retryQueue = [];
					this.updateStatus(cardId, 'idle');
				}
			});
		}
	}

	/**
	 * Get or create state for a card.
	 */
	private getState(cardId: string): CardPersistenceState {
		let state = this.state.get(cardId);
		if (!state) {
			state = {
				cardId,
				serverRevisions: null,
				contentSnapshotVersion: null,
				redoTargets: [],
				pendingChange: null,
				retryQueue: [],
				status: 'idle',
				debounceTimer: null,
				maxWaitTimer: null,
			};
			this.state.set(cardId, state);
		}
		return state;
	}

	/**
	 * Clear debounce timers for a card.
	 */
	private clearTimers(cardId: string): void {
		const state = this.state.get(cardId);
		if (state) {
			if (state.debounceTimer) {
				clearTimeout(state.debounceTimer);
				state.debounceTimer = null;
			}
			if (state.maxWaitTimer) {
				clearTimeout(state.maxWaitTimer);
				state.maxWaitTimer = null;
			}
		}
	}

	/**
	 * Update status and notify listeners.
	 */
	private updateStatus(
		cardId: string,
		newStatus: CardPersistenceState['status'],
		error?: Error | RevisionConflictError
	): void {
		const state = this.getState(cardId);
		const previousStatus = state.status;
		state.status = newStatus;

		if (error) {
			state.lastError = error;
		}

		// Notify listeners
		const event: PersistenceStateChangeEvent = {
			cardId,
			previousStatus,
			currentStatus: newStatus,
			error,
			revisions: state.serverRevisions ?? undefined,
		};

		this.stateChangeCallbacks.forEach(cb => {
			try {
				cb(event);
			} catch {
				// Ignore callback errors
			}
		});
	}

	/**
	 * Notify listeners of successful persistence.
	 */
	private notifyPersisted(event: ChangePersistedEvent): void {
		this.persistedCallbacks.forEach(cb => {
			try {
				cb(event);
			} catch {
				// Ignore callback errors
			}
		});
	}

	private notifyRestoreNavigation(cardId: string): void {
		const event: RestoreNavigationChangedEvent = { cardId };
		this.restoreNavigationCallbacks.forEach(cb => {
			try {
				cb(event);
			} catch {
				// Ignore callback errors
			}
		});
	}

	private mergePendingCardPatch(
		existing: Partial<CardConfig> | undefined,
		incoming: Partial<CardConfig>
	): Partial<CardConfig> {
		if (!existing) {
			return { ...incoming };
		}
		const merged: Record<string, unknown> = { ...existing, ...incoming };

		for (const key of [
			'data',
			'style',
			'visualization',
			'presentation',
			'features',
		] as const) {
			const existingValue = existing[key];
			const incomingValue = incoming[key];
			if (
				existingValue &&
				incomingValue &&
				typeof existingValue === 'object' &&
				!Array.isArray(existingValue) &&
				typeof incomingValue === 'object' &&
				!Array.isArray(incomingValue)
			) {
				merged[key] = this.deepMerge(
					existingValue as Record<string, unknown>,
					incomingValue as Record<string, unknown>
				);
			} else if (incomingValue !== undefined) {
				merged[key] = incomingValue;
			}
		}

		return merged as Partial<CardConfig>;
	}

	private deepMerge(
		base: Record<string, unknown>,
		incoming: Record<string, unknown>
	): Record<string, unknown> {
		const result: Record<string, unknown> = { ...base };
		for (const key of Object.keys(incoming)) {
			const baseValue = base[key];
			const incomingValue = incoming[key];
			if (
				baseValue !== null &&
				typeof baseValue === 'object' &&
				!Array.isArray(baseValue) &&
				incomingValue !== null &&
				typeof incomingValue === 'object' &&
				!Array.isArray(incomingValue)
			) {
				result[key] = this.deepMerge(
					baseValue as Record<string, unknown>,
					incomingValue as Record<string, unknown>
				);
			} else {
				result[key] = incomingValue;
			}
		}
		return result;
	}

	/**
	 * Check if error is a revision conflict.
	 */
	private isRevisionConflictError(
		error: unknown
	): error is RevisionConflictError {
		return (
			typeof error === 'object' &&
			error !== null &&
			'type' in error &&
			(error as RevisionConflictError).type === 'REVISION_CONFLICT'
		);
	}
}
